import React, { useMemo } from 'react';
import { SVGIcon } from '../../SVGIcon/SVGIcon';
import clsx from 'clsx';
import { APP_COLORS } from '@/config/colors';

export interface EstimationCardProps {
  /** Percentage value to display */
  percentage: string;
  /** Main title of the card */
  title: string;
  /** Price value with currency */
  price: string;
  /** Description text */
  description: string;
  /** Color variant of the card */
  variant?: 'green' | 'red';
  /** Additional CSS classes */
  className?: string;
}

const variantStyles = {
  green: {
    border: 'border-green-200',
    iconBg: 'bg-green-200',
    iconFill: APP_COLORS.green[700],
    price: 'text-green-700',
    checkFill: 'green-700' as const,
  },
  red: {
    border: 'border-red-200',
    iconBg: 'bg-red-200',
    iconFill: 'red-800' as const,
    price: 'text-red-800',
    checkFill: 'red-800' as const,
  },
};

/**
 * EstimationCard displays estimation details with an icon, percentage, title, price and description
 */
export const EstimationCard: React.FC<EstimationCardProps> = ({
  percentage,
  title,
  price,
  description,
  variant = 'green',
  className = '',
}) => {
  // Define colors based on variant

  const styles = useMemo(() => variantStyles[variant], [variant]);
  return (
    <div
      className={clsx(
        'flex w-full flex-col items-start rounded-card border bg-white px-6 py-5 lg:flex-row lg:gap-6',
        styles.border,
        className,
      )}
    >
      {/* Top section with icon and percentage */}
      <div className="flex gap-4 items-center mb-4 lg:mb-5">
        {/* Chart icon */}
        <div
          className={clsx(
            'flex h-10 w-10 items-center justify-center rounded-[10px] p-1',
            styles.iconBg,
          )}
        >
          {variant === 'green' ? (
            <SVGIcon name="chart-upward" fill={styles.iconFill} />
          ) : (
            <SVGIcon name="chart-arrow" fill={styles.iconFill} />
          )}
        </div>
        {/* Percentage */}
        <div className="font-inter text-[22px] font-extrabold leading-5 text-text-primary">
          {percentage}
        </div>
      </div>

      <div>
        {/* Middle section with title and price */}
        <div className="mb-4 flex items-center gap-[14px] lg:mb-5">
          <div className="flex flex-col gap-1">
            <div className="font-inter text-sm font-semibold leading-[18px] text-text-primary">
              {title}
            </div>
            <div
              className={clsx(
                'font-inter text-base font-extrabold leading-5',
                styles.price,
              )}
            >
              {price}
            </div>
          </div>
        </div>

        {/* Bottom section with check icon and description */}
        {description ? (
          <div className="flex items-start lg:gap-3">
            {variant === 'green' ? (
              <SVGIcon
                name="check-circle"
                className="mr-2 mt-0.5 flex-shrink-0"
                size={'sm'}
              />
            ) : (
              <SVGIcon
                name="error-circle"
                className="mr-2 mt-0.5 flex-shrink-0"
                size={'sm'}
              />
            )}

            <div className="font-inter text-[13px] font-normal leading-[18px] text-grayText">
              {description}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
